import { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { getConfiguracionIndGob, getCatalogoFiltroIndGob, getIndicadoresGob } from '../../api/indicadorGubernamental/indGobApi';
import { AuthContext } from '../../context/AuthContext';

const EMPTY_DATASET = {
  jerarquiaMpio: [],
  jerarquiaMpioAct: [],
  jerarquiaMpioEps: [],
  jerarquiaEpsMpio: [],
  jerarquiaEpsAct: [],
  matrizCumplEps: [],
  matrizCumplMpio: [],
};

const SECCION_MAP = {
  gob_mpio:     { key: 'jerarquiaMpio',     tipo: 'barra' },
  gob_mpio_act: { key: 'jerarquiaMpioAct',  tipo: 'barra' },
  gob_mpio_eps: { key: 'jerarquiaMpioEps',  tipo: 'barra' },
  gob_eps_mpio: { key: 'jerarquiaEpsMpio',  tipo: 'barra' },
  gob_eps_act:  { key: 'jerarquiaEpsAct',   tipo: 'barra' },
  gob_cumpl_eps:  { key: 'matrizCumplEps',  tipo: 'matriz' },
  gob_cumpl_mpio: { key: 'matrizCumplMpio', tipo: 'matriz' },
};

const normalizeRows = (arr) => (arr || []).map((item) => {
  const normalized = {};
  Object.entries(item).forEach(([k, v]) => {
    const lowerK = String(k).toLowerCase();
    if (
      typeof v === 'string'
      && v.trim() !== ''
      && !Number.isNaN(Number(v))
    ) {
      normalized[lowerK] = Number(v);
    } else {
      normalized[lowerK] = v;
    }
  });
  return normalized;
});

const useIndGobData = () => {
  const { sedeActiva } = useContext(AuthContext);

  const [config, setConfig] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [filtrosValores, setFiltrosValores] = useState({});
  const [catalogos, setCatalogos] = useState({});
  const [indicadoresData, setIndicadoresData] = useState({ ...EMPTY_DATASET });
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingIndicadores, setLoadingIndicadores] = useState(false);
  const [error, setError] = useState(null);

  const fetchCounterRef = useRef(0);
  const prevCatalogoParamsRef = useRef({});
  const initialFetchRef = useRef(false);
  const cacheByTabRef = useRef({});
  const configRef = useRef([]);
  const filtrosValoresRef = useRef({});
  const activeTabRef = useRef(null);

  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { filtrosValoresRef.current = filtrosValores; }, [filtrosValores]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  const buildFiltrosForSeccion = useCallback((tabClave) => {
    const seccion = (configRef.current || []).find((s) => s.clave_tecnica === tabClave);
    const filtrosSeccion = seccion?.filtros || [];
    const out = {};
    const valoresAcumuladosPorCampo = {};
    filtrosSeccion.forEach((f) => {
      const readKey = (f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin')
        ? f.nombre_campo
        : f.clave_vinculacion;
      const rawValor = filtrosValoresRef.current[readKey];
      const valorString = Array.isArray(rawValor) ? rawValor.join(',') : (rawValor || '');
      if (!valorString) return;
      const bucket = valoresAcumuladosPorCampo[f.nombre_campo]
        || (valoresAcumuladosPorCampo[f.nombre_campo] = []);
      valorString.split(',').forEach((v) => {
        const t = v.trim();
        if (t) bucket.push(t);
      });
    });
    Object.entries(valoresAcumuladosPorCampo).forEach(([campo, valores]) => {
      const unicos = Array.from(new Set(valores));
      out[campo] = unicos.join(',');
    });
    return out;
  }, []);

  const fingerprintFor = useCallback((tabClave) => {
    const filtros = buildFiltrosForSeccion(tabClave);
    return JSON.stringify(filtros);
  }, [buildFiltrosForSeccion]);

  const cargarIndicadoresSeccion = useCallback(async (tabClave) => {
    if (!tabClave) return;

    const fingerprint = fingerprintFor(tabClave);
    const cached = cacheByTabRef.current[tabClave];
    if (cached && cached.fingerprint === fingerprint) {
      setIndicadoresData((prev) => ({ ...prev, ...cached.payload }));
      return;
    }

    const filtros = buildFiltrosForSeccion(tabClave);
    const myFetchId = ++fetchCounterRef.current;

    setLoadingIndicadores(true);
    try {
      const response = await getIndicadoresGob(filtros, tabClave);
      const res = response?.data || response || {};

      if (myFetchId !== fetchCounterRef.current) return;

      const partial = { ...EMPTY_DATASET };
      Object.keys(SECCION_MAP).forEach((sk) => {
        const key = SECCION_MAP[sk].key;
        if (Array.isArray(res[key])) partial[key] = normalizeRows(res[key]);
      });

      cacheByTabRef.current[tabClave] = { fingerprint, payload: partial };

      setIndicadoresData((prev) => ({ ...prev, ...partial }));
    } catch (err) {
      console.error('useIndGobData.cargarIndicadores error raw:', err);
      const data = err?.response?.data;
      if (data) {
        console.error('useIndGobData.cargarIndicadores backend message:', data.message);
        console.error('useIndGobData.cargarIndicadores backend success/errors:', JSON.stringify({
          success: data.success,
          errors: data.errors,
          debug: data.debug ?? null
        }));
      }
      setError('Error al cargar indicadores gubernamentales');
    } finally {
      if (myFetchId === fetchCounterRef.current) {
        setLoadingIndicadores(false);
      }
    }
  }, [buildFiltrosForSeccion, fingerprintFor]);

  const cargarCatalogosDeTab = useCallback(async (tabClave) => {
    const seccion = configRef.current.find((s) => s.clave_tecnica === tabClave);
    if (!seccion) return;

    let catCargados = 0;
    const nuevosDefaults = {};
    const nuevosCatalogos = {};
    let catalogoCambiado = false;

    for (const filtro of seccion.filtros) {
      if (!filtro.query_catalogo) {
        catCargados++;
        continue;
      }

      try {
        const params = {};
        if (filtro.id_filtro_padre) {
          const padre = seccion.filtros.find((f) => f.id === filtro.id_filtro_padre);
          if (padre) {
            const readKeyPadre = (padre.nombre_campo === 'edadIni' || padre.nombre_campo === 'edadFin')
              ? padre.nombre_campo
              : padre.clave_vinculacion;
            const valorPadre = filtrosValoresRef.current[readKeyPadre];
            if (valorPadre) {
              params[padre.nombre_campo] = Array.isArray(valorPadre) ? valorPadre.join(',') : valorPadre;
            } else {
              continue;
            }
          }
        }

        const paramsString = JSON.stringify(params);
        if (prevCatalogoParamsRef.current[filtro.id] === paramsString) {
          catCargados++;
          continue;
        }
        prevCatalogoParamsRef.current[filtro.id] = paramsString;

        const res = await getCatalogoFiltroIndGob(filtro.id, params);
        const nuevaData = res.data || [];

        const isMarcarActivo = filtro.marcar_x_defecto === true
          || filtro.marcar_x_defecto === 1
          || String(filtro.marcar_x_defecto) === '1';

        if (filtro.tipo_control === 'select_multiple'
          && isMarcarActivo
          && filtrosValoresRef.current[filtro.clave_vinculacion] === undefined) {
          let valorDefault;
          const isSedeGlobal = String(filtro.clave_vinculacion).toUpperCase() === 'SEDE_GLOBAL'
            || String(filtro.nombre_campo || '').toLowerCase() === 'sede';
          if (isSedeGlobal && sedeActiva?.id != null) {
            const idSedeActiva = String(sedeActiva.id);
            const existeEnCatalogo = nuevaData.some((item) => String(item.id) === idSedeActiva);
            valorDefault = existeEnCatalogo ? [idSedeActiva] : [];
          } else {
            valorDefault = nuevaData
              .map((item) => String(item.id || item.eps || item.codigo))
              .filter(Boolean);
          }
          if (valorDefault.length > 0) {
            nuevosDefaults[filtro.clave_vinculacion] = valorDefault;
          }
        }

        const prevStr = JSON.stringify(catalogos[filtro.id]);
        const nextStr = JSON.stringify(nuevaData);
        if (prevStr !== nextStr) {
          nuevosCatalogos[filtro.id] = nuevaData;
          catalogoCambiado = true;
        }
        catCargados++;
      } catch (err) {
        console.warn(`Error en catálogo ${filtro.label}`, err);
      }
    }

    if (Object.keys(nuevosDefaults).length > 0) {
      setFiltrosValores((prev) => {
        const merged = { ...prev };
        Object.entries(nuevosDefaults).forEach(([k, v]) => {
          if (merged[k] === undefined) merged[k] = v;
        });
        filtrosValoresRef.current = merged;
        return merged;
      });
    }

    if (catalogoCambiado) {
      setCatalogos((prev) => ({ ...prev, ...nuevosCatalogos }));
    }

    if (catCargados === seccion.filtros.length && !initialFetchRef.current) {
      initialFetchRef.current = true;
      setTimeout(() => { cargarIndicadoresSeccion(tabClave); }, 0);
    }
  }, [catalogos, cargarIndicadoresSeccion]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await getConfiguracionIndGob();
        const data = res.data || res;
        setConfig(data);
        configRef.current = data;
        if (data.length > 0) {
          const primera = data[0].clave_tecnica;
          setActiveTab(primera);
          activeTabRef.current = primera;
        }
      } catch (err) {
        setError('No se pudo cargar la configuración de Indicador Gubernamental');
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (!sedeActiva?.id) return;
    const allFiltros = (configRef.current || []).flatMap((s) => s.filtros || []);
    const filtroSede = allFiltros.find((f) =>
      String(f.nombre_campo || '').toLowerCase() === 'sede'
      || String(f.clave_vinculacion || '').toUpperCase() === 'SEDE_GLOBAL'
    );
    if (!filtroSede) return;
    const keySede = filtroSede.clave_vinculacion;
    if (!filtrosValoresRef.current[keySede]) {
      const sedeId = String(sedeActiva.id);
      setFiltrosValores((prev) => {
        const next = { ...prev, [keySede]: [sedeId] };
        filtrosValoresRef.current = next;
        return next;
      });
    }
  }, [sedeActiva]);

  useEffect(() => {
    if (!activeTab || loadingConfig) return;
    cargarCatalogosDeTab(activeTab);
  }, [activeTab, loadingConfig, cargarCatalogosDeTab]);

  useEffect(() => {
    if (!activeTab || loadingConfig) return;
    cargarCatalogosDeTab(activeTab);
  }, [filtrosValores, activeTab, loadingConfig, cargarCatalogosDeTab]);

  const handleSetActiveTab = useCallback((clave) => {
    setActiveTab(clave);
    activeTabRef.current = clave;
    if (clave) cargarIndicadoresSeccion(clave);
  }, [cargarIndicadoresSeccion]);

  const handleFiltroChange = useCallback((key, val) => {
    setFiltrosValores((prev) => {
      const normalizedVal = Array.isArray(val)
        ? val.map((v) => String(v))
        : val;
      const next = { ...prev, [key]: normalizedVal };
      filtrosValoresRef.current = next;
      return next;
    });
  }, []);

  const aplicarFiltros = useCallback(() => {
    const currentTab = activeTabRef.current;
    if (!currentTab) return;
    cacheByTabRef.current[currentTab] = undefined;
    cargarIndicadoresSeccion(currentTab);
  }, [cargarIndicadoresSeccion]);

  const limpiarFiltros = useCallback(() => {
    setFiltrosValores({});
    filtrosValoresRef.current = {};
    cacheByTabRef.current = {};
    prevCatalogoParamsRef.current = {};
    initialFetchRef.current = false;
  }, []);

  const buildTree = useCallback((rows) => {
    if (!rows || rows.length === 0) return { roots: [], map: new Map() };
    const map = new Map();
    rows.forEach((r) => {
      const id = String(r.id ?? r.ID ?? r.nombre ?? Math.random());
      map.set(id, { ...r, _id: id, children: [] });
    });
    const roots = [];
    map.forEach((node) => {
      const pid = node.id_padre ?? node.idPadre ?? null;
      if (pid == null || pid === '' || !map.has(String(pid))) {
        roots.push(node);
      } else {
        map.get(String(pid)).children.push(node);
      }
    });
    const sortRec = (arr) => {
      arr.sort((a, b) => (Number(a.orden ?? 0) - Number(b.orden ?? 0)) || String(a.nombre ?? '').localeCompare(String(b.nombre ?? '')));
      arr.forEach((n) => sortRec(n.children));
    };
    sortRec(roots);
    return { roots, map };
  }, []);

  const buildMatriz = useCallback((rowsLargo) => {
    if (!rowsLargo || rowsLargo.length === 0) return { actividades: [], cols: [], grid: new Map(), totalsRow: null, totalsCol: new Map(), totalGlobal: null };
    const actMap = new Map();
    const colMap = new Map();
    const grid = new Map();
    (rowsLargo || []).forEach((r) => {
      const actKey = String(r.actividad || '');
      if (!actMap.has(actKey)) {
        actMap.set(actKey, { key: actKey, label: String(r.actividad_label || r.actividad || actKey), orden: Number(r.orden_act || 0) });
      }
      const colKey = String(r.col_key || '');
      if (!colMap.has(colKey)) {
        colMap.set(colKey, { key: colKey, label: String(r.col_label || r.col_key || colKey), orden: Number(r.orden_col || 0) });
      }
      const gKey = `${actKey}__${colKey}`;
      grid.set(gKey, {
        total: Number(r.total || 0),
        realizado: Number(r.realizado || 0),
        valor: Number(r.valor ?? (Number(r.realizado || 0) / Math.max(1, Number(r.total || 1)))),
      });
    });
    const actividades = Array.from(actMap.values()).sort((a, b) => a.orden - b.orden || a.label.localeCompare(b.label));
    const cols = Array.from(colMap.values())
      .filter((c) => c.key !== '__TOTAL__')
      .sort((a, b) => a.orden - b.orden || a.label.localeCompare(b.label));
    const tieneTotalCol = colMap.has('__TOTAL__');
    if (tieneTotalCol) cols.push(colMap.get('__TOTAL__'));

    const totalsCol = new Map();
    cols.forEach((c) => {
      let sumR = 0;
      let sumT = 0;
      actividades.forEach((a) => {
        const g = grid.get(`${a.key}__${c.key}`);
        if (g) { sumR += g.realizado; sumT += g.total; }
      });
      totalsCol.set(c.key, { total: sumT, realizado: sumR, valor: sumR / Math.max(1, sumT) });
    });

    let totalsRow = null;
    if (actividades.length > 0 && actividades[actividades.length - 1].key === '__TOTAL__') {
      const t = actividades.pop();
      totalsRow = t;
    }

    let totalGlobal = null;
    if (totalsRow && tieneTotalCol) {
      const g = grid.get('__TOTAL____TOTAL__');
      if (g) totalGlobal = g;
    }
    return { actividades, cols, grid, totalsRow, totalsCol, totalGlobal };
  }, []);

  const datasetActivo = useMemo(() => {
    if (!activeTab) return null;
    const meta = SECCION_MAP[activeTab];
    if (!meta) return null;
    const rows = indicadoresData[meta.key] || [];
    if (meta.tipo === 'barra') {
      const tree = buildTree(rows);
      return { tipo: 'barra', meta, ...tree };
    }
    const mat = buildMatriz(rows);
    return { tipo: 'matriz', meta, ...mat };
  }, [activeTab, indicadoresData, buildTree, buildMatriz]);

  const seccionInfo = useMemo(() => {
    if (!activeTab || !config) return null;
    return config.find((s) => s.clave_tecnica === activeTab) || null;
  }, [activeTab, config]);

  return {
    config,
    activeTab,
    setActiveTab: handleSetActiveTab,
    filtrosValores,
    catalogos,
    loadingConfig,
    loadingIndicadores,
    error,
    handleFiltroChange,
    aplicarFiltros,
    limpiarFiltros,
    datasetActivo,
    seccionInfo,
  };
};

export default useIndGobData;
